<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="resources-container">
    <v-toolbar dense color="transparent" class="content-max-width elevation-0 pb-16 mt-8 mb-2">
      <v-tabs v-model="tab" background-color="transparent" class="tabs">
        <v-tab>{{ $t('tabs.msTeams.tabName') }}</v-tab>
      </v-tabs>
    </v-toolbar>
    <section class="scrollable-section">
      <v-tabs-items v-model="tab" class="tab-content content-max-width">
        <v-tab-item>
          <div class="resource-card">
            <div class="tab-title">{{ $t('tabs.msTeams.title') }}</div>
            <p class="tab-description" v-html="$t('tabs.msTeams.description')" />
            <v-form ref="form">
              <v-text-field
                v-model="msTeamsConfiguration.orgShort"
                class="form-field"
                disabled
                :label="$t('tabs.msTeams.package.orgShort', { name: organizationName })"
              />
              <v-text-field
                v-model="msTeamsConfiguration.tenantId"
                class="form-field"
                :rules="[guidRule]"
                placeholder="00000000-0000-0000-0000-000000000000"
                :label="$t('tabs.msTeams.package.tenantId')"
              />
              <v-text-field
                v-model="msTeamsConfiguration.applicationId"
                class="form-field"
                :rules="[guidRule]"
                placeholder="00000000-0000-0000-0000-000000000000"
                :label="$t('tabs.msTeams.package.applicationId')"
              />
              <v-text-field
                v-model="msTeamsConfiguration.objectId"
                class="form-field"
                :rules="[guidRule]"
                placeholder="00000000-0000-0000-0000-000000000000"
                :label="$t('tabs.msTeams.package.objectId')"
              />

              <div class="resource-card-actions">
                <v-btn
                  class="primary"
                  :disabled="!allAppPackageDataIsCorrect || !msTeamsConfigurationIsSaved"
                  @click="downloadMsTeamsPackage"
                >
                  {{ $t('tabs.msTeams.download') }}
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <v-btn class="ml-4" @click="toggleHelp">
                  {{ $t('tabs.msTeams.help.button') }}
                  <v-icon>mdi-help-circle</v-icon>
                </v-btn>
                <v-spacer />
                <v-btn class="primary" @click="saveMsTeamsConfiguration">
                  {{ $t('tabs.msTeams.save') }}
                </v-btn>
              </div>
            </v-form>

            <div class="help" :hidden="!help">
              <v-expansion-panels accordion>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('tabs.msTeams.help.step1.title') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="help-content">
                    <ul>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <li v-for="(item, i) in 6" :key="i" v-html="$t(`tabs.msTeams.help.step1.subSteps[${i}]`)"></li>
                    </ul>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('tabs.msTeams.help.step2.title') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="help-content">
                    <ul>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <li v-for="(item, i) in 2" :key="i" v-html="$t(`tabs.msTeams.help.step2.subSteps[${i}]`)"></li>
                    </ul>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('tabs.msTeams.help.step3.title') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="help-content">
                    <ul>
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <li v-for="(item, i) in 7" :key="i" v-html="$t(`tabs.msTeams.help.step3.subSteps[${i}]`)"></li>
                    </ul>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ResourcesService from '@/services/resource.service';

const KNOWN_HOSTNAMES = {
  PROD: {
    E_AGENT: 'ecs-web.evolveip.net',
    E_ADMIN: 'ecs-admin.evolveip.net'
  },
  BETA: {
    E_AGENT: 'ecs-awc-beta.evolveip.net',
    E_ADMIN: 'ecs-web-admin-beta.evolveip.net'
  },
  DEV: {
    E_AGENT: 'ecs-web-client-dev-ecs-phl01.evolveip.net',
    E_ADMIN: 'ecs-web-admin-dev-ecs-phl01.evolveip.net'
  },
  QA: {
    E_AGENT: 'ecs-web-client-qa-ecs-phl01.evolveip.net',
    E_ADMIN: 'ecs-web-admin-qa-ecs-phl01.evolveip.net'
  },
  LOCAL: {
    E_AGENT: 'vm-server-07.voip.lab.evolveip.net',
    E_ADMIN: 'localhost:8080'
  }
};

export default {
  name: 'ContactManagement',

  components: {},

  data() {
    return {
      isLoading: false,
      tab: 0,
      msTeamsConfiguration: {
        tenantId: '',
        applicationId: '',
        objectId: '',
        orgShort: '',
        baseUrl: 'https://ecs-web.evolveip.net',
        redirectUrl: '',
        notificationsUrl: ''
      },
      msTeamsConfigurationIsSaved: false,
      availableConfigurations: [],
      help: false,
      guidRule: value => {
        const pattern = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

        if (!value) {
          return true;
        } else if (!pattern.test(value)) {
          return 'Invalid GUID format';
        } else {
          return true;
        }
      }
    };
  },

  watch: {
    organizationName() {
      this.msTeamsConfiguration.orgShort = this.organizationAbbreviation;
    }
  },

  computed: {
    ...mapGetters(['userInfo', 'organizationId', 'organizationName', 'organizationAbbreviation']),

    allAppPackageDataIsCorrect() {
      if (
        !this.msTeamsConfiguration.applicationId ||
        !this.msTeamsConfiguration.objectId ||
        !this.msTeamsConfiguration.orgShort ||
        !this.msTeamsConfiguration.baseUrl
      ) {
        return false;
      }

      const validationRules = [
        this.guidRule(this.msTeamsConfiguration.applicationId),
        this.guidRule(this.msTeamsConfiguration.objectId)
      ];

      return !validationRules.some(rule => rule !== true);
    }
  },

  mounted() {
    this.prefillMsTeamsConfiguration();
  },

  beforeDestroy() {},

  created() {},

  methods: {
    ...mapActions([
      'retrieveMSTeamsConfiguration',
      'updateMSTeamsConfiguration',
      'snackbarSuccessMessage',
      'snackbarErrorMessage'
    ]),

    async prefillMsTeamsConfiguration() {
      const orgId = this.organizationId;
      const eAdminHostname = window.location.hostname;

      const msTeamsConfiguration = await this.retrieveMSTeamsConfiguration(orgId);
      if (msTeamsConfiguration) {
        this.msTeamsConfiguration = msTeamsConfiguration;
      }

      let eAgentHostname = KNOWN_HOSTNAMES.PROD.E_AGENT;
      let notificationHostname = KNOWN_HOSTNAMES.PROD.E_AGENT;

      switch (eAdminHostname) {
        case KNOWN_HOSTNAMES.BETA.E_ADMIN:
          eAgentHostname = KNOWN_HOSTNAMES.BETA.E_AGENT;
          notificationHostname = KNOWN_HOSTNAMES.BETA.E_AGENT;
          break;
        case KNOWN_HOSTNAMES.DEV.E_ADMIN:
          eAgentHostname = KNOWN_HOSTNAMES.DEV.E_AGENT;
          notificationHostname = KNOWN_HOSTNAMES.DEV.E_AGENT;
          break;
        case KNOWN_HOSTNAMES.QA.E_ADMIN:
          eAgentHostname = KNOWN_HOSTNAMES.QA.E_AGENT;
          notificationHostname = KNOWN_HOSTNAMES.QA.E_AGENT;
          break;
        case KNOWN_HOSTNAMES.LOCAL.E_ADMIN:
          eAgentHostname = KNOWN_HOSTNAMES.LOCAL.E_AGENT;
          notificationHostname = 'bf229e4441af.ngrok.app';
          break;
      }

      this.msTeamsConfiguration.orgShort = this.msTeamsConfiguration.orgShort || this.organizationAbbreviation;
      this.msTeamsConfiguration.baseUrl = this.msTeamsConfiguration.baseUrl || `https://${eAgentHostname}`;
      this.msTeamsConfiguration.redirectUrl = this.msTeamsConfiguration.redirectUrl || `https://${eAgentHostname}`;
      this.msTeamsConfiguration.notificationsUrl =
        this.msTeamsConfiguration.notificationsUrl ||
        `https://${notificationHostname}/WebhookHandler/MSGraph/TeamsPresence`;
      this.msTeamsConfigurationIsSaved =
        !!this.msTeamsConfiguration.tenantId &&
        !!this.msTeamsConfiguration.applicationId &&
        !!this.msTeamsConfiguration.objectId;
    },

    saveMsTeamsConfiguration() {
      this.updateMSTeamsConfiguration({
        orgId: this.organizationId,
        ...this.msTeamsConfiguration
      })
        .then(() => {
          this.snackbarSuccessMessage(this.$t('tabs.msTeams.messages.config.update.success'));
          this.msTeamsConfigurationIsSaved = true;
        })
        .catch(error =>
          this.snackbarErrorMessage(this.$t('tabs.msTeams.messages.config.update.error', { error: error.message }))
        );
    },

    downloadMsTeamsPackage() {
      ResourcesService.createMicrosoftTeamsPackage(this.msTeamsConfiguration)
        .then(this.createPackage)
        .catch(console.log);
    },

    createPackage(blob) {
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', 'teams.package.zip');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    },

    toggleHelp() {
      this.help = !this.help;
    }
  }
};
</script>

<style scoped lang="scss">
.resource-card {
  padding: 12px 28px 32px;
  border-radius: 8px;
  background-color: #fff1;
  max-width: 720px;
}
.scrollable-section {
  height: calc(100vh - 168px);
  overflow: auto;
}
.tab-content {
  min-height: calc(100vh - 208px);
  background: transparent;
}
.content-max-width {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
.tab-title {
  font-weight: bold;
  font-size: 20px;
  margin: 20px 0 16px;
}
.tab-description {
  max-width: 65ch;
  opacity: 0.75;
}
.v-btn .v-icon {
  padding-left: 8px;
  opacity: 0.75;
}
.form-field {
  max-width: 400px;
}
.help {
  line-height: 22px;
  padding: 20px 0;
}
h4 {
  padding: 20px 0 12px;
}
.help-content {
  color: #fff9;
  font-size: 14px;
}
.resource-card-actions {
  display: flex;
  margin-top: 24px;
}
</style>
